// ─────────────────────────────────────────────────────────────────────────────
// Criar um ticket NOVO a partir do atendimento em curso.
//
// Acontece o tempo todo: o cliente liga por um problema e, no meio da conversa,
// aparece outro. Abrir o segundo à mão significa sair do Orion, achar o
// cliente, redigitar tudo. Aqui o ticket novo HERDA o solicitante, o CNPJ e o
// setor do ticket de origem — o operador só descreve a situação nova.
//
// A resolução dos códigos é uma CASCATA, e a ordem foi ganha na prática: o
// valor mais específico primeiro, o mais genérico por último, e um padrão que
// nunca deixa o ticket sair sem código.
//
// Este módulo é puro (não conhece React). Quem chama é components/NovoTicket.js.
// ─────────────────────────────────────────────────────────────────────────────
// Só dados e constantes: este arquivo é PURO de propósito, para as regras
// poderem ser testadas sem o app em volta (scripts/teste-novo-ticket.mjs).
// As duas chamadas de rede do fluxo moram em services/helpdeskApi.js.
import { MAPA_CATEGORIAS } from '../config/categorias.js';
import { STATUS, OPERADOR } from './codigosMysuite.js';

/** Setor padrão quando nada mais responde: Suporte. */
export const SETOR_PADRAO = 3;
/** Categoria padrão: Space Guardian, que é o grosso dos atendimentos. */
export const CATEGORIA_PADRAO = 2;

/**
 * Status e operador do ticket novo, pelo tipo de atendimento.
 * As mesmas regras do envio de atualização — é a tabela mestra do 1.4.1, e ela
 * precisa continuar valendo nos três lugares que a usam.
 */
export function statusEOperador(tipo, { codigoOperadorLogado = 0, destinoSuporteAvancado } = {}) {
  switch (tipo) {
    case 'Conclusão':
      return { codigoStatus: STATUS.CONCLUSAO, codigoOperador: codigoOperadorLogado };
    case 'Aguardando interação de cliente':
      return { codigoStatus: STATUS.AGUARDANDO_CLIENTE, codigoOperador: codigoOperadorLogado };
    case 'Setor de sistemas':
      return { codigoStatus: STATUS.SETOR_SISTEMAS, codigoOperador: OPERADOR.SETOR_SISTEMAS };
    case 'Suporte avançado':
      return {
        codigoStatus: STATUS.SUPORTE_AVANCADO,
        codigoOperador: destinoSuporteAvancado || OPERADOR.ANALISE_SUPORTE,
      };
    case 'Comercial':
      return { codigoStatus: STATUS.COMERCIAL, codigoOperador: codigoOperadorLogado };
    case 'Na fila para retorno':
      return { codigoStatus: STATUS.FILA_RETORNO, codigoOperador: codigoOperadorLogado };
    default:
      // "Em atendimento" e "Atualização" caem aqui: o ticket nasce com o
      // operador que o criou, já em atendimento.
      return { codigoStatus: STATUS.EM_ATENDIMENTO, codigoOperador: codigoOperadorLogado };
  }
}

/**
 * Setor do ticket novo.
 * ticket de origem → pendente conhecido → derivado do nome → Suporte.
 */
export function resolverSetor({ ticketInfo, pendente } = {}) {
  const doTicket = parseInt(ticketInfo?.codigoSetor, 10);
  if (doTicket) return doTicket;

  const doPendente = parseInt(pendente?.codigoSetor, 10);
  if (doPendente) return doPendente;

  const nome = String(ticketInfo?.setor || '').toUpperCase();
  if (nome.includes('AUTOMA')) return 15;      // "AUTOMAÇÃO" com ou sem cedilha
  if (nome.includes('SUPORTE')) return 3;

  return SETOR_PADRAO;
}

/**
 * Categoria (produto) do ticket novo.
 *
 * A API do MySuite devolve o NOME da categoria, não o código — por isso o
 * mapa. Sem ele, o ticket novo saía sempre no produto padrão, ainda que o
 * de origem fosse de outro sistema.
 */
export function resolverCategoria({ ticketInfo, pendente } = {}) {
  const doPendente = parseInt(pendente?.codigoCategoria, 10);
  if (doPendente) return doPendente;

  const doTicket = parseInt(ticketInfo?.codigoCategoria, 10);
  if (doTicket) return doTicket;

  const nome = String(ticketInfo?.categoria || '').trim();
  if (nome) {
    const exato = MAPA_CATEGORIAS[nome];
    if (exato) return exato;
    // Comparação frouxa: o MySuite às vezes muda a caixa ou o espaçamento.
    const alvo = nome.toLowerCase();
    const achado = Object.entries(MAPA_CATEGORIAS)
      .find(([chave]) => chave.toLowerCase() === alvo);
    if (achado) return achado[1];
  }

  return CATEGORIA_PADRAO;
}

/** Prioridade: do ticket → do pendente → 1 (baixa). */
export function resolverPrioridade({ ticketInfo, pendente } = {}) {
  return parseInt(ticketInfo?.prioridade, 10)
    || parseInt(pendente?.prioridade, 10)
    || 1;
}

/**
 * O que impede o ticket de ser criado.
 * Devolve a mensagem, ou null quando está tudo certo.
 *
 * POR QUE O CÓDIGO DO SOLICITANTE É OBRIGATÓRIO
 *
 * Até 22/08/2026 o CNPJ sozinho bastava: sem o código, o ticket saía com
 * `codigoUsuarioCliente: 0` e quem escolhia o solicitante era o MySuite, pelo
 * CNPJ. Só que um CNPJ tem vários contatos — e o escolhido raramente era o do
 * ticket de origem.
 *
 * Era a causa do "o Orion troca o solicitante ao criar ticket novo": um defeito
 * intermitente, porque só aparecia quando a resolução automática falhava (ticket
 * fora da fila de pendentes, API lenta, ticket já concluído).
 *
 * O ticket novo continua o mesmo atendimento, com a mesma pessoa. Abrir para
 * outro contato do cliente não é um detalhe de cadastro: o solicitante não fica
 * sabendo do próprio chamado, e quem recebe não sabe do que se trata.
 *
 * Por isso o CNPJ deixou de ser alternativa. Quando o código não é resolvido
 * sozinho, a tela pede que o operador informe — ver components/NovoTicket.js.
 */
export function motivoParaNaoCriar({ titulo, interacao, codigoUsuarioCliente, cnpjCliente }) {
  if (!String(titulo || '').trim()) return 'Escreva o título do ticket novo.';
  if (!String(interacao || '').trim()) return 'Descreva a nova situação.';

  if (!parseInt(codigoUsuarioCliente, 10)) {
    return String(cnpjCliente || '').trim()
      // Há CNPJ, e é justamente por isso que o aviso precisa ser explícito: é o
      // caso em que antes o ticket era criado para a pessoa errada.
      ? 'O solicitante do ticket de origem não foi identificado. Informe o código '
        + 'do solicitante — criar pelo CNPJ abriria o ticket para outro contato '
        + 'do mesmo cliente.'
      : 'O solicitante do ticket de origem não foi identificado. Informe o código '
        + 'do solicitante para continuar.';
  }

  return null;
}

/**
 * O número do ticket criado, procurado na resposta do n8n.
 *
 * O fluxo devolve em formatos diferentes conforme o caminho que tomou. Testar
 * os nomes conhecidos e, por último, procurar a primeira sequência de 5 a 8
 * dígitos é o que funciona para todos eles — é assim desde a 1.x.
 */
export function numeroDaResposta(dados) {
  const obj = Array.isArray(dados) ? dados[0] : dados;
  if (obj == null) return null;

  if (typeof obj === 'number') return String(obj);
  if (typeof obj === 'string') {
    const achado = obj.match(/(\d{5,8})/);
    return achado ? achado[1] : null;
  }

  for (const chave of ['codigoTicket', 'numero', 'codigo', 'ticket', 'numero_ticket']) {
    const v = obj[chave];
    if (v != null && String(v).trim()) {
      const achado = String(v).match(/(\d{5,8})/);
      if (achado) return achado[1];
    }
  }

  const achado = JSON.stringify(obj).match(/(\d{5,8})/);
  return achado ? achado[1] : null;
}
